// Wire format of the Taiko Controller's tuning channel, mirroring
// main/taiko_host_config.{h,c} in the firmware.
//
// The board owns the drum type, which DIP1 and DIP2 select from the shape of
// the waveform in front of them. Everything about amplitude - the per-channel
// gains and the detector's level thresholds - lives here, because it can only
// honestly be set by measuring a real drum.
//
// Carried as a vendor-defined HID feature report on the Arcade interface.
// Feature items add no Input items to the descriptor, so the gamepad axes the
// arcade loader reads are untouched.

import { CHANNELS_PER_PLAYER, PLAYER_COUNT } from "./taikoGameEmulation";

export const CONFIG_REPORT_ID = 0x10;
export const CONFIG_VERSION = 1;
export const CONFIG_REPORT_SIZE = 36;

const HEADER_BYTES = 4;

export const CMD_APPLY = 0;
export const CMD_SAVE = 1;
export const CMD_RESET = 2;

export const STATUS_P1_LONG_TAIL = 1 << 0;
export const STATUS_P2_LONG_TAIL = 1 << 1;
export const STATUS_STORED = 1 << 2;
export const STATUS_TWO_PLAYERS = 1 << 3;

// Matches the firmware's clamp ranges; anything outside them is rewritten by
// the board, so the page must not offer values it would silently lose.
export const GAIN_Q8_MIN = 16;
export const GAIN_Q8_MAX = 4096;
export const GAIN_Q8_ONE = 256;
export const NOISE_FLOOR_MAX = 255;
export const FULL_SCALE_MIN = 64;
export const FULL_SCALE_MAX = 4095;
export const TRIGGER_MIN = 8;
export const RELEASE_MIN = 4;

// Practical slider span. The wire range runs wider, but a drum needing more
// than a quarter or four times nominal wants a hardware fix, not a slider.
export const GAIN_UI_MIN = 0.25;
export const GAIN_UI_MAX = 4;

export const gainToMultiplier = (q8) => q8 / GAIN_Q8_ONE;
export const multiplierToGain = (multiplier) =>
    Math.round(multiplier * GAIN_Q8_ONE);

const clamp = (value, min, max) => Math.min(max, Math.max(min, Math.round(value)));

/** Shipping values, matching taiko_hit_default_config() in the firmware. */
export const defaultPlayerTuning = () => ({
    gains: new Array(CHANNELS_PER_PLAYER).fill(GAIN_Q8_ONE),
    noiseFloor: 18,
    trigger: 48,
    release: 34,
    fullScale: 1360,
});

export const defaultBoardConfig = () => ({
    players: Array.from({ length: PLAYER_COUNT }, defaultPlayerTuning),
    status: 0,
});

/** Same ordering as the firmware: outermost bound first, so the pair cannot invert. */
export const clampPlayerTuning = (tuning) => {
    const fullScale = clamp(tuning.fullScale, FULL_SCALE_MIN, FULL_SCALE_MAX);
    const trigger = clamp(tuning.trigger, TRIGGER_MIN, fullScale - 1);
    const release = clamp(tuning.release, RELEASE_MIN, trigger - 1);
    return {
        gains: tuning.gains.map((gain) => clamp(gain, GAIN_Q8_MIN, GAIN_Q8_MAX)),
        noiseFloor: clamp(tuning.noiseFloor, 0, NOISE_FLOOR_MAX),
        trigger,
        release,
        fullScale,
    };
};

export const clampBoardConfig = (config) => ({
    ...config,
    players: config.players.map(clampPlayerTuning),
});

/** Build the 36 byte payload written to the feature report. */
export const encodeBoardConfig = (config, command = CMD_APPLY) => {
    const buffer = new ArrayBuffer(CONFIG_REPORT_SIZE);
    const view = new DataView(buffer);
    view.setUint8(0, CONFIG_VERSION);
    view.setUint8(1, command);
    view.setUint8(2, 0);
    view.setUint8(3, 0);

    let offset = HEADER_BYTES;
    for (let player = 0; player < PLAYER_COUNT; player++) {
        const tuning = clampPlayerTuning(config.players[player]);
        for (let channel = 0; channel < CHANNELS_PER_PLAYER; channel++) {
            view.setUint16(offset, tuning.gains[channel], true);
            offset += 2;
        }
        view.setUint16(offset, tuning.noiseFloor, true);
        offset += 2;
        view.setUint16(offset, tuning.trigger, true);
        offset += 2;
        view.setUint16(offset, tuning.release, true);
        offset += 2;
        view.setUint16(offset, tuning.fullScale, true);
        offset += 2;
    }
    return new Uint8Array(buffer);
};

/**
 * Parse what the board reports back. Returns null for a short buffer or an
 * unrecognised schema version, which is how firmware without the tuning
 * channel shows up.
 */
export const decodeBoardConfig = (view) => {
    if (!view) {
        return null;
    }
    // WebHID is asymmetric here: sendFeatureReport() takes the payload without
    // the report ID, but receiveFeatureReport() resolves with the ID as byte 0
    // whenever the device uses numbered reports. Accept either shape rather
    // than depending on which browser is doing the reading.
    const base =
        view.byteLength >= CONFIG_REPORT_SIZE + 1 &&
        view.getUint8(0) === CONFIG_REPORT_ID
            ? 1
            : 0;
    if (view.byteLength - base < CONFIG_REPORT_SIZE ||
        view.getUint8(base) !== CONFIG_VERSION) {
        return null;
    }
    const status = view.getUint8(base + 2);
    const players = [];
    let offset = base + HEADER_BYTES;
    for (let player = 0; player < PLAYER_COUNT; player++) {
        const gains = [];
        for (let channel = 0; channel < CHANNELS_PER_PLAYER; channel++) {
            gains.push(view.getUint16(offset, true));
            offset += 2;
        }
        const noiseFloor = view.getUint16(offset, true);
        offset += 2;
        const trigger = view.getUint16(offset, true);
        offset += 2;
        const release = view.getUint16(offset, true);
        offset += 2;
        const fullScale = view.getUint16(offset, true);
        offset += 2;
        players.push({ gains, noiseFloor, trigger, release, fullScale });
    }
    return { players, status };
};

export const playerIsLongTail = (status, player) =>
    (status & (player === 0 ? STATUS_P1_LONG_TAIL : STATUS_P2_LONG_TAIL)) !== 0;
